// Shared admission pool for active dotLottie WASM players (#409, epic #412).
//
// `dotlottie_player_controller` already animates ONLY on-screen cosmetics
// (#407: IntersectionObserver builds the player on enter, destroys it on
// leave). But a single dense screen can have many cosmetics on-screen AT ONCE
// (a long feed, the live-chat roster, a followers grid). Without a cap, every
// visible avatar spins up its own WASM player → CPU/GPU/battery spike, worst
// on mobile.
//
// This pool caps the number of SIMULTANEOUSLY LIVE players. A controller asks
// the pool for a slot before building its player; if the cap is already
// reached it stays on its static poster (#406, zero WASM cost) and joins a
// FIFO waiting queue. When a live player is torn down (its element scrolled
// off, or the element disconnected) it releases its slot and the pool promotes
// the next waiting member — which then builds its player and swaps poster →
// canvas (the same transition as a normal scroll-in, so no flicker).
//
// Replacement policy: admission + promote-on-release (NOT forced eviction).
// Scrolling naturally drives it — elements leaving the viewport release slots
// in visibility order, and entering ones acquire them, so the live set tracks
// what's on screen. We deliberately do NOT evict a still-visible, still-playing
// member to admit a newer one: that would stop a mid-animation player and read
// as a flicker. The cap is a ceiling on concurrency, not a strict "newest N".
//
// Member contract (the controller implements these):
//   - onPoolAdmit()    : called when promoted from the queue — build/resume.
//   - stillWantsSlot() : true while the element is on-screen, connected, and
//                        the tab is foregrounded; checked before promoting so a
//                        member that scrolled off while queued is skipped.

// Max live WASM players at once. Tuned for a dense mobile feed: high enough
// that a normal screenful all animates, low enough to bound CPU/battery. Single
// source of truth — change here only.
export const MAX_ACTIVE_PLAYERS = 10

class DotLottiePool {
  constructor(cap) {
    this.cap = cap
    this.active = new Set()
    this.waiting = []
  }

  // Request a slot for `member`. Returns true if admitted (the caller may build
  // / resume its player now). Returns false if the pool is full — the caller
  // stays on its poster; `member.onPoolAdmit()` fires later when a slot frees.
  acquire(member) {
    if (this.active.has(member)) return true
    this.#dequeue(member)

    if (this.active.size < this.cap) {
      this.active.add(member)
      return true
    }
    this.waiting.push(member)
    return false
  }

  // Release `member`'s slot (or drop it from the queue). If it held a live
  // slot, promote the next waiting member(s) up to the cap. Idempotent — safe
  // to call for a member that never acquired.
  release(member) {
    const wasActive = this.active.delete(member)
    this.#dequeue(member)
    if (wasActive) this.#promote()
  }

  #dequeue(member) {
    const i = this.waiting.indexOf(member)
    if (i !== -1) this.waiting.splice(i, 1)
  }

  #promote() {
    while (this.active.size < this.cap && this.waiting.length > 0) {
      const next = this.waiting.shift()
      if (!next) continue
      // Skip members that scrolled off / disconnected while queued — they no
      // longer want a slot, so they free it for the next in line.
      if (typeof next.stillWantsSlot === "function" && !next.stillWantsSlot()) {
        continue
      }
      this.active.add(next)
      try {
        next.onPoolAdmit()
      } catch (_) {
        // A broken member must not stall the queue: drop it and keep promoting.
        this.active.delete(next)
      }
    }
  }

  // Introspection for tests / debugging.
  activeCount() { return this.active.size }
  waitingCount() { return this.waiting.length }
}

// Process-wide singleton — every controller instance shares this one pool.
export const dotLottiePool = new DotLottiePool(MAX_ACTIVE_PLAYERS)

// Expose for headless smoke tests (assert ≤ cap live players). Harmless in
// prod; it's just a reference to the same singleton.
if (typeof window !== "undefined") {
  window.__dotLottiePool = dotLottiePool
}
